import { waitForCondition } from './wait-for-condition';
import { BaseComponent2 } from './base-component2';
import { RELOAD_COMPONENT_TOPIC } from '../../constants';
import { ReloadEventData } from '../../ui-aggregator/client/types';
import { DeferredPromise } from './client-component-runner';
import { ClientComponentLogger } from '../../client-component-logger';

export class BaseComponent2Factory {
    constructor(
        private readonly componentFactory: () => BaseComponent2,
        private readonly initLock: DeferredPromise,
        private readonly logger: ClientComponentLogger
    ) {}

    public async initAndBuildComponents() {
        await waitForCondition(() => document.readyState !== 'loading');
        await this.componentFactory().onFirstPageLoadOncePerComponentType();
        const components = await this.buildComponentsByTagsInDOM();
        this.registerBuilderToComponentReload();
        return components;
    }

    private async buildComponentsByTagsInDOM() {
        const components: Array<BaseComponent2> = [];
        const rootSelector = this.componentFactory().rootSelector;
        const rootElements = Array.from(document.querySelectorAll(rootSelector));

        for (const rootElement of rootElements) {
            try {
                const component = this.componentFactory() as BaseComponent2;
                await component.setLogger(this.logger);

                component.setRootElement(rootElement as HTMLElement);
                rootElement.classList.remove(component.rootSelector.substring(1));

                await this.registerHandlerForComponentReload(component);
                await this.runInitialization(component);
                components.push(component);
            } catch (e) {
                this.logger.logError({ error: e as Error });
            }
        }
        return components;
    }

    private registerBuilderToComponentReload() {
        o_global.eventQBus.on(RELOAD_COMPONENT_TOPIC, this.buildComponentsByTagsInDOMAtComponentReload());
    }

    private buildComponentsByTagsInDOMAtComponentReload() {
        return async (_e: ReloadEventData) => {
            await this.buildComponentsByTagsInDOM();
        };
    }

    protected async runInitialization(component: BaseComponent2): Promise<void> {
        try {
            await component.init();
            this.initLock.resolve();
        } catch (e) {
            this.logger.logError({ error: e as Error, type: 'OnInitError' });
        }
    }

    protected async registerHandlerForComponentReload(component: BaseComponent2): Promise<void> {
        const reloadCallback = o_global.eventQBus.on(RELOAD_COMPONENT_TOPIC, this.handleComponentReload(component));
        reloadCallback && window?.o_product?.reloadCallbacks.push(reloadCallback);
    }

    private handleComponentReload(component: BaseComponent2) {
        return async (_e: ReloadEventData) => {
            try {
                await this.waitForInitialization();
                await component.onReloadComponentEvent();
            } catch (e) {
                this.logger.logError({ error: e as Error, type: 'OnReloadComponentEvent' });
            }
        };
    }

    /**
 *
 */
    private async waitForInitialization(): Promise<void> {
        await this.initLock.promise;
    }
}
